import { useEffect, useState } from "react";
import { Download, FileText, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { money, monthLabel, downloadCSV } from "../lib/format";
import { Panel, Empty } from "./Widgets";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { BrandBadge } from "./Brands";
import { CompanyOptions } from "./Companies";

const sel = "h-8 rounded-md bg-[#0B0F17] border border-slate-700 text-xs px-2";

function Filters({ f, set, months, agents }) {
  const ch = (k) => (e) => set({ ...f, [k]: e.target.value });
  return (
    <div className="flex flex-wrap gap-2 p-4 border-b border-slate-800 bg-[#0d1320]">
      <select data-testid="range-from" value={f.from_month} onChange={ch("from_month")} className={sel}>{months.map((m) => <option key={m} value={m}>From {monthLabel(m)}</option>)}</select>
      <select data-testid="range-to" value={f.to_month} onChange={ch("to_month")} className={sel}>{months.map((m) => <option key={m} value={m}>To {monthLabel(m)}</option>)}</select>
      <select data-testid="range-brand" value={f.brand} onChange={ch("brand")} className={sel}>
        <option value="">Crumble + Zappay</option><option value="Crumble">Crumble only</option><option value="Zappay">Zappay only</option><option value="none">Untagged only</option>
      </select>
      <select data-testid="range-company" value={f.company} onChange={ch("company")} className={sel}>
        <CompanyOptions />
      </select>
      <select data-testid="range-agent" value={f.agent_id} onChange={ch("agent_id")} className={sel}>
        <option value="">All agents</option>{agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
    </div>
  );
}

export function RangeReport({ className = "", company = "" }) {
  const [months, setMonths] = useState([]);
  const [agents, setAgents] = useState([]);
  const [f, setF] = useState(null);
  const [d, setD] = useState(null);
  const [send, setSend] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/agents").then((r) => setAgents(r.data));
    api.get("/dashboard").then((r) => {
      const ms = [...(r.data.months || [])].sort();
      setMonths(ms);
      if (ms.length) setF({ from_month: ms[0], to_month: ms[ms.length - 1], brand: "", company, agent_id: "" });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setF((x) => (x ? { ...x, company } : x)); }, [company]);
  const params = f ? Object.fromEntries(Object.entries(f).filter(([, v]) => v)) : null;
  useEffect(() => {
    if (!params) return undefined;
    let live = true;
    api.get("/range-report", { params }).then((r) => live && setD(r.data)).catch((e) => live && toast.error(errMsg(e)));
    return () => { live = false; };
  }, [JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps
  const pdf = async () => {
    try {
      const r = await api.get("/range-report/pdf", { params, responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `residual-report-${f.from_month}-to-${f.to_month}.pdf`; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const csv = () => downloadCSV(`residual-report-${f.from_month}-to-${f.to_month}.csv`, ["Merchant", "Merchant ID", "Brand", "Company", "Belongs to", "Months", "Residual", "Agent payout"],
    d.merchants.map((m) => [m.merchant_name, m.merchant_id, m.brand || "", m.company, m.owner, m.months, m.residual, m.payout]));
  const doSend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/range-report/send", { ...params, agent_ids: send });
      const sent = data.results.filter((r) => r.status === "sent").length;
      toast.success(`Sent to ${sent} agent${sent === 1 ? "" : "s"}${data.results.length > sent ? ` · ${data.results.length - sent} skipped` : ""}`);
      data.results.filter((r) => r.status !== "sent").forEach((r) => toast.message(`${r.agent}: ${r.status} (${r.reason})`));
      setSend([]);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!f) return <Panel title="Residual report builder" className={className}><Empty>No reports uploaded yet.</Empty></Panel>;
  const realAgents = (d?.agents || []).filter((a) => agents.some((x) => x.id === a.agent_id && x.email));
  return (
    <Panel title="Residual report builder" className={className} action={d && (
      <div className="flex gap-2">
        <Button data-testid="range-csv" size="sm" variant="outline" onClick={csv}><Download size={13} className="mr-1" />CSV</Button>
        <Button data-testid="range-pdf" size="sm" variant="outline" onClick={pdf}><FileText size={13} className="mr-1" />PDF</Button>
      </div>)}>
      <Filters f={f} set={setF} months={months} agents={agents} />
      {!d ? <Empty>Loading…</Empty> : (
        <div className="p-4 space-y-5" data-testid="range-report">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[["Residual", d.totals.residual, "range-total-residual"], ["Agent payout", d.totals.payout, "range-total-payout"],
              ["Crumble", d.by_brand.Crumble, "range-crumble"], ["Zappay", d.by_brand.Zappay, "range-zappay"]].map(([l, v, id]) => (
              <div key={id} className="rounded border border-slate-800 bg-[#0B0F17] p-3"><div className="text-[11px] font-mono uppercase text-slate-500">{l}</div>
                <div className="font-mono text-lg text-slate-100" data-testid={id}>{money(v)}</div></div>))}
          </div>
          {d.by_brand.Untagged > 0 && <div className="text-xs text-amber-300" data-testid="range-untagged-note">{money(d.by_brand.Untagged)} is from merchants not yet tagged Crumble or Zappay.</div>}
          <table className="tbl w-full" data-testid="range-months-table">
            <thead><tr><th>Month</th><th className="!text-right">Volume</th><th className="!text-right">Residual</th><th className="!text-right">Agent payout</th></tr></thead>
            <tbody>{d.months.map((m) => <tr key={m.month}><td className="font-mono">{monthLabel(m.month)}</td><td className="text-right font-mono text-slate-400">{money(m.volume)}</td>
              <td className="text-right font-mono">{money(m.residual)}</td><td className="text-right font-mono text-emerald-400">{money(m.payout)}</td></tr>)}</tbody>
          </table>
          {!f.agent_id && (
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <span className="text-xs text-slate-400 flex-1">Tick agents to email each one their own part of this report.</span>
                <Button data-testid="range-send" size="sm" disabled={!send.length || busy} onClick={doSend} className="bg-blue-500 hover:bg-blue-600 text-white">
                  <Send size={13} className="mr-1" />{busy ? "Sending…" : `Send to ${send.length || ""} agent${send.length === 1 ? "" : "s"}`}</Button>
              </div>
              <table className="tbl w-full" data-testid="range-agents-table">
                <thead><tr><th className="w-8"><Checkbox data-testid="range-send-all" checked={realAgents.length > 0 && send.length === realAgents.length}
                  onCheckedChange={(v) => setSend(v ? realAgents.map((a) => a.agent_id) : [])} /></th><th>Agent</th><th className="!text-right">Residual</th><th className="!text-right">Payout</th></tr></thead>
                <tbody>{d.agents.map((a) => {
                  const ag = agents.find((x) => x.id === a.agent_id);
                  const real = !!ag;
                  return <tr key={a.agent_id} data-testid={`range-agent-${a.agent_id}`}>
                    <td>{real && <Checkbox data-testid={`range-send-${a.agent_id}`} checked={send.includes(a.agent_id)} disabled={!ag.email}
                      title={ag.email ? "" : "No email on file"}
                      onCheckedChange={(v) => setSend((s) => (v ? [...s, a.agent_id] : s.filter((x) => x !== a.agent_id)))} />}</td>
                    <td>{a.name}{real && !ag.email && <span className="ml-2 text-[10px] text-slate-500">no email on file</span>}</td><td className="text-right font-mono">{money(a.residual)}</td><td className="text-right font-mono text-emerald-400">{money(a.payout)}</td></tr>;
                })}</tbody>
              </table>
            </div>
          )}
          <details>
            <summary className="cursor-pointer text-xs text-slate-400" data-testid="range-merchants-toggle">{d.merchants.length} merchants</summary>
            <table className="tbl w-full mt-2" data-testid="range-merchants-table">
              <thead><tr><th>Merchant</th><th>Brand</th><th>Company</th><th>Belongs to</th><th className="!text-right">Residual</th><th className="!text-right">Payout</th></tr></thead>
              <tbody>{d.merchants.map((m) => <tr key={m.merchant_id}><td>{m.merchant_name || "—"}<div className="font-mono text-[11px] text-slate-500">{m.merchant_id}</div></td>
                <td><BrandBadge brand={m.brand} /></td><td className="text-slate-400">{m.company}</td><td className="text-slate-300">{m.owner}</td>
                <td className={`text-right font-mono ${m.residual < 0 ? "text-rose-400" : ""}`}>{money(m.residual)}</td><td className="text-right font-mono text-emerald-400">{money(m.payout)}</td></tr>)}</tbody>
            </table>
          </details>
        </div>
      )}
    </Panel>
  );
}
